import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, 
  faArrowLeft, 
  faTimes, 
  faInfoCircle,
  faUpload,
  faFile,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';
import { ADMIN_URLS, getResourceUrl } from '../../config/apiConfig';

function EditResource() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    resource_type: 'document',
    url: '',
    is_public: true,
    program_id: null
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentFileName, setCurrentFileName] = useState('');
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const fetchResourceAndPrograms = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        // Fetch resource details
        const resourceData = await adminService.getResource(id);
        setFormData({
          resource_type: resourceData.resource_type || 'document',
          url: resourceData.url || '',
          is_public: resourceData.is_public !== undefined ? resourceData.is_public : true,
          program_id: resourceData.program_id || null
        });
        
        // Extract filename from url if it exists
        if (resourceData.url) {
          if (resourceData.url.startsWith('/uploads/')) {
            // It's a local file, extract filename
            const urlParts = resourceData.url.split('/');
            setCurrentFileName(urlParts[urlParts.length - 1]);
          } else if (resourceData.url.startsWith('http')) {
            // It's an external URL, show as external link
            setCurrentFileName('External Link');
          }
        }
        
        // Fetch programs for dropdown
        const programsResponse = await fetch(`${ADMIN_URLS.PROGRAMS}?status=completed`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
          }
        });
        
        if (!programsResponse.ok) {
          throw new Error('Failed to fetch programs');
        }
        
        const programsData = await programsResponse.json();
        setPrograms(programsData.items || []);
      } catch (err) {
        console.error("Error fetching data:", err);
        setLoadError("Failed to load resource data. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchResourceAndPrograms();
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB limit
        setFileError('File size should not exceed 10MB');
        setFile(null);
      } else {
        setFileError(null);
        setFile(selectedFile);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.resource_type) {
      newErrors.resource_type = 'Resource type is required';
    }
    
    if (!formData.url && !file) {
      newErrors.url = 'Either provide a URL or upload a file';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSaving(true);
    try {
      let resourceData = { ...formData };
      
      if (file) {
        // If there's a file to upload, use FormData
        const formDataToSend = new FormData();
        formDataToSend.append('file', file);
        formDataToSend.append('resource_type', formData.resource_type);
        formDataToSend.append('is_public', formData.is_public.toString());
        if (formData.program_id) {
          formDataToSend.append('program_id', formData.program_id);
        }
        
        // Custom file upload request
        const response = await fetch(`${ADMIN_URLS.RESOURCE_DETAIL(id)}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
            // Don't set Content-Type for FormData - browser will set it with boundary
          },
          body: formDataToSend
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update resource');
        }
        
        // Update was successful
        setSuccessMessage('Resource updated successfully!');
        setTimeout(() => {
          navigate(`/admin/resources/${id}`);
        }, 1500);
      } else {
        // Standard JSON update without file
        await adminService.updateResource(id, resourceData);
        setSuccessMessage('Resource updated successfully!');
        setTimeout(() => {
          navigate(`/admin/resources/${id}`);
        }, 1500);
      }
    } catch (err) {
      console.error("Error updating resource:", err);
      setErrors({
        ...errors,
        submit: err.message || 'Failed to update resource. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.size > 10 * 1024 * 1024) {
        setFileError('File size should not exceed 10MB');
        setFile(null);
      } else {
        setFileError(null);
        setFile(droppedFile);
      }
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading-container">
          <div className="admin-spinner"></div>
          <p>Loading resource data...</p>
        </div>
      </AdminLayout>
    );
  }

  if (loadError) {
    return (
      <AdminLayout>
        <div className="admin-error-container">
          <FontAwesomeIcon icon={faInfoCircle} size="2x" />
          <p>{loadError}</p>
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Edit Resource</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard" className="admin-breadcrumb-link">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/resources" className="admin-breadcrumb-link">Resources</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to={`/admin/resources/${id}`} className="admin-breadcrumb-link">Resource Details</Link>
          </li>
          <li className="admin-breadcrumb-item active">
            <span className="admin-breadcrumb-link">Edit</span>
          </li>
        </ul>
      </div>
      
      {successMessage && (
        <div className="admin-alert admin-alert-success" style={{ marginBottom: '1rem' }}>
          <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.5rem' }} />
          {successMessage}
        </div>
      )}
      
      {errors.submit && (
        <div className="admin-alert admin-alert-danger" style={{ marginBottom: '1rem' }}>
          {errors.submit}
        </div>
      )}
      
      <div className="admin-card">
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            {/* Classification Section */}
            <div className="admin-row">
              <div className="admin-col-12">
                <div className="admin-form-group">
                  <label htmlFor="resource_type" className="admin-form-label">Resource Type *</label>
                  <select
                    id="resource_type"
                    name="resource_type"
                    value={formData.resource_type}
                    onChange={handleInputChange}
                    className={`admin-form-control ${errors.resource_type ? 'admin-is-invalid' : ''}`}
                  >
                    <option value="">Select Resource Type</option>
                    <option value="document">Document</option>
                    <option value="presentation">Presentation</option>
                    <option value="video">Video</option>
                    <option value="audio">Audio</option>
                    <option value="link">External Link</option>
                  </select>
                  {errors.resource_type && <div className="admin-invalid-feedback">{errors.resource_type}</div>}
                </div>
              </div>
            </div>
            
            <div className="admin-form-group">
              <label htmlFor="program_id" className="admin-form-label">Associated Program</label>
              <select
                id="program_id"
                name="program_id"
                value={formData.program_id || ''}
                onChange={handleInputChange}
                className="admin-form-control"
              >
                <option value="">No Associated Program</option>
                {programs.map(program => (
                  <option key={program.id} value={program.id}>{program.title}</option>
                ))}
              </select>
              <small className="admin-form-text admin-text-muted">
                Optional: Select a program to associate with this resource
              </small>
            </div>
            
            {/* Resource Source Section */}
            <div className="admin-form-group">
              <label htmlFor="url" className="admin-form-label">Resource URL</label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                className={`admin-form-control ${errors.url ? 'admin-is-invalid' : ''}`}
                placeholder="https://example.com/resource"
              />
              <small className="admin-form-text admin-text-muted">
                Enter a valid URL to link to an external resource
              </small>
              {errors.url && <div className="admin-invalid-feedback">{errors.url}</div>}
            </div>
            
            {currentFileName && (
              <div className="admin-form-group">
                <label className="admin-form-label">Current Resource</label>
                <div className="admin-file-selected">
                  <div className="admin-file-info">
                    <FontAwesomeIcon 
                      icon={formData.url.startsWith('http') ? faExternalLinkAlt : faFile} 
                      className="admin-file-icon" 
                    />
                    <span className="admin-file-name">{currentFileName}</span>
                    {formData.url.startsWith('/uploads/') && (
                      <a 
                        href={getResourceUrl(formData.url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{
                          marginLeft: '10px',
                          color: 'var(--primary-color)',
                          textDecoration: 'none',
                          fontSize: '12px'
                        }}
                      >
                        <FontAwesomeIcon icon={faExternalLinkAlt} style={{marginRight: '4px'}} />
                        View
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <div className="admin-form-group">
              <label className="admin-form-label">Upload New File</label>
              <div className="admin-file-upload-container">
                <input 
                  ref={fileInputRef}
                  type="file" 
                  id="file" 
                  onChange={handleFileChange}
                  className="admin-file-input"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.mp4,.mp3,.zip,.rar"
                />
                <label htmlFor="file" className="admin-file-upload-label">
                  <div className="admin-file-upload-content">
                    <FontAwesomeIcon icon={faUpload} className="admin-file-upload-icon" />
                    <div className="admin-file-upload-text">
                      <strong>Click to select file</strong> or drag & drop
                    </div>
                    <small className="admin-file-upload-help">
                      Supported: PDF, DOC, PPT, XLS, Images, Videos, Audio (Max: 10MB)
                    </small>
                  </div>
                </label>
                
                {file && (
                  <div className="admin-file-selected">
                    <div className="admin-file-info">
                      <FontAwesomeIcon icon={faFile} className="admin-file-icon" />
                      <span className="admin-file-name">{file.name}</span>
                      <span className="admin-file-size">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                    <button 
                      type="button" 
                      onClick={() => setFile(null)}
                      className="admin-file-remove"
                      title="Remove file"
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                )}
                
                {fileError && (
                  <div className="admin-invalid-feedback" style={{ display: 'block', marginTop: '0.5rem' }}>
                    {fileError}
                  </div>
                )}
              </div>
              <small className="admin-form-text admin-text-muted">
                Uploading a new file will replace the current one
              </small>
            </div>
            
            {/* Settings Section */}
            <div className="admin-form-group">
              <div className="admin-checkbox-wrapper">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_public"
                    checked={formData.is_public}
                    onChange={handleInputChange}
                    className="admin-checkbox"
                  />
                  <span className="admin-checkbox-text">Make this resource public</span>
                </label>
                <small className="admin-form-text admin-text-muted">
                  <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: '0.25rem' }} />
                  Public resources are visible to everyone, including non-members
                </small>
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
              <Link to={`/admin/resources/${id}`} className="admin-btn admin-btn-light">
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5rem' }} />
                Back to Resource
              </Link>
              
              <button 
                type="submit"
                className="admin-btn admin-btn-primary"
                disabled={isSaving}
              >
                <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      <style jsx>{`
        .admin-file-upload-container {
          border: 2px dashed #ced4da;
          border-radius: 8px;
          background-color: #f8f9fa;
          transition: all 0.3s ease;
          position: relative;
        }
        
        .admin-file-upload-container:hover {
          border-color: #007bff;
          background-color: #f0f8ff;
        }
        
        .admin-file-input {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }
        
        .admin-file-upload-label {
          display: block;
          padding: 2rem;
          cursor: pointer;
          text-align: center;
        }
        
        .admin-file-upload-content {
          pointer-events: none;
        }
        
        .admin-file-upload-icon {
          font-size: 2rem;
          color: #6c757d;
          margin-bottom: 0.5rem;
          display: block;
        }
        
        .admin-file-upload-text {
          margin-bottom: 0.5rem;
          color: #495057;
        }
        
        .admin-file-upload-help {
          color: #6c757d;
        }
        
        .admin-file-selected {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background-color: #e9ecef;
          border-radius: 4px;
          border: 1px solid #ced4da;
          margin-top: 1rem;
        }
        
        .admin-file-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .admin-file-icon {
          color: #007bff;
        }
        
        .admin-file-name {
          font-weight: 500;
          color: #495057;
        }
        
        .admin-file-size {
          color: #6c757d;
          font-size: 0.875rem;
        }
        
        .admin-file-remove {
          background: none;
          border: none;
          color: #dc3545;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          transition: background-color 0.2s ease;
        }
        
        .admin-file-remove:hover {
          background-color: #f8d7da;
        }
        
        .admin-checkbox-wrapper {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        
        .admin-checkbox-label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          font-weight: 500;
        }
        
        .admin-checkbox {
          margin: 0;
        }
        
        .admin-checkbox-text {
          margin: 0;
        }
        
        .admin-alert {
          padding: 1rem;
          border-radius: 4px;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .admin-alert-danger {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }
        
        .admin-alert-success {
          background-color: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }
      `}</style>
    </AdminLayout>
  );
}

export default EditResource;
